import { z } from 'zod';
/**
 * The one list of languages. Web, API, worker and domain read it; nothing else enumerates
 * languages. A language is added by a language pack (docs/partial/localization-spec.md 6.7) and an
 * entry here.
 *
 * - `nativeName` is what the language switcher shows (never a flag).
 * - `englishName` is what an AI prompt calls the language.
 * - Tier 1: everything, including the rule-based parsers (number words, voice commands,
 *   dictation). Tier 2: UI, AI and server texts; parsers fall back to the generic rules.
 */
export const LOCALE_CODES = ['de', 'en'];
export const localeSchema = z.enum(LOCALE_CODES);
export const LOCALES = {
    de: {
        code: 'de',
        nativeName: 'Deutsch',
        englishName: 'German',
        tier: 1,
        defaultFormatLocale: 'de-DE',
    },
    en: {
        code: 'en',
        nativeName: 'English',
        englishName: 'English',
        tier: 1,
        defaultFormatLocale: 'en-US',
    },
};
export const SUPPORTED_LOCALES = LOCALE_CODES;
/** What a missing translation falls back to, and the language translators start from. */
export const FALLBACK_LOCALE = 'en';
/** Build-time default; an instance overrides it with DEFAULT_LOCALE. */
export const DEFAULT_LOCALE = 'de';
export function isLocale(value) {
    return typeof value === 'string' && LOCALE_CODES.includes(value);
}
/** Any stored or received language tag → a supported language (`de-AT` → `de`), else the fallback. */
export function toLocale(value, fallback = DEFAULT_LOCALE) {
    const language = value?.trim().toLowerCase().split(/[-_]/)[0];
    return isLocale(language) ? language : fallback;
}
/**
 * The first enabled language a client asks for. `preferred` is a list of BCP 47 tags in order
 * (navigator.languages) or an Accept-Language header value.
 */
export function negotiateLocale(preferred, enabled = SUPPORTED_LOCALES, fallback = DEFAULT_LOCALE) {
    const tags = typeof preferred === 'string'
        ? preferred
            .split(',')
            .map((part) => {
            const [tag, ...params] = part.trim().split(';');
            const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
            return { tag: tag ?? '', q: q ? Number(q.slice(2)) : 1 };
        })
            .filter((entry) => entry.tag && entry.tag !== '*' && !(entry.q <= 0))
            .sort((a, b) => b.q - a.q)
            .map((entry) => entry.tag)
        : (preferred ?? []);
    for (const tag of tags) {
        const language = tag.trim().toLowerCase().split(/[-_]/)[0];
        if (isLocale(language) && enabled.includes(language))
            return language;
    }
    return enabled.includes(fallback) ? fallback : (enabled[0] ?? fallback);
}
/**
 * The locale for `Intl`: the UI language with the browser's region when both speak the same
 * language (`de` + `de-AT` → `de-AT`), else the language's default region.
 */
export function formatLocaleFor(locale, browserLanguages) {
    for (const tag of browserLanguages ?? []) {
        const [language, region] = tag.trim().split(/[-_]/);
        if (language?.toLowerCase() === locale && region && /^[A-Za-z]{2}$/.test(region))
            return `${locale}-${region.toUpperCase()}`;
    }
    return LOCALES[locale].defaultFormatLocale;
}
export const localeInfoSchema = z.object({
    code: localeSchema,
    nativeName: z.string(),
    tier: z.union([z.literal(1), z.literal(2)]),
});
//# sourceMappingURL=locales.js.map